/**
 * PHASE 1: Graffiti™ Scrambler Hardware Checkout
 * 
 * This component handles one-time product purchases with:
 * - Shipping address collection
 * - Automatic tax calculation via Stripe Tax
 * - Secure payment processing
 * - Order confirmation
 * 
 * Integration: Drop this into your SynthetIQ Signals® React app
 */

import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const StripeCheckout = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Product catalog for Graffiti™ Scrambler and accessories
  const products = [
    {
      id: 'scrambler-base',
      name: 'Graffiti™ Scrambler - Base Unit',
      description: 'Express secure app-distribution unit with AI model deployment capabilities',
      price: 79900, // $799.00 in cents — matches backend/app/catalog/synthetiq_catalog.py HARDWARE_CATALOG
      image: '/images/scrambler-unit.png',
      category: 'hardware'
    },
    {
      id: 'scrambler-pro',
      name: 'Graffiti™ Scrambler - Pro Unit',
      description: 'Advanced unit with extended storage and processing power',
      price: 119900, // $1,199.00 in cents — matches backend/app/catalog/synthetiq_catalog.py HARDWARE_CATALOG
      image: '/images/scrambler-pro.png',
      category: 'hardware'
    },
    {
      id: 'usb-c-cable',
      name: 'USB-C Power Cable',
      description: 'High-speed USB-C cable for Scrambler unit',
      price: 1900, // $19.00 in cents
      image: '/images/usb-cable.png',
      category: 'accessory'
    },
    {
      id: 'mounting-kit',
      name: 'Wall Mounting Kit',
      description: 'Secure mounting hardware for Scrambler unit',
      price: 2900, // $29.00 in cents
      image: '/images/mount-kit.png',
      category: 'accessory'
    }
  ];

  /**
   * Creates a Stripe Checkout Session for one-time payment
   * Includes shipping and automatic tax calculation
   */
  const handleCheckout = async (product) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: product.id,
          productName: product.name,
          productPrice: product.price,
          productDescription: product.description,
          productImage: product.image,
          mode: 'payment', // One-time payment (not subscription)
          collectShipping: true, // Collect shipping address
          automaticTax: true // Enable Stripe Tax
        }),
      });

      const { sessionId, error: apiError } = await response.json();

      if (apiError) {
        throw new Error(apiError);
      }

      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (stripeError) {
        throw stripeError;
      }
    } catch (err) {
      setError(err.message);
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stripe-checkout-container">
      <div className="product-grid">
        {/* Hardware Products Section */}
        <div className="product-section">
          <h2 className="section-title">Graffiti™ Scrambler Units</h2>
          <div className="products">
            {products
              .filter(p => p.category === 'hardware')
              .map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onCheckout={handleCheckout}
                  loading={loading}
                />
              ))}
          </div>
        </div>

        {/* Accessories Section */}
        <div className="product-section">
          <h2 className="section-title">Accessories</h2>
          <div className="products">
            {products
              .filter(p => p.category === 'accessory')
              .map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onCheckout={handleCheckout}
                  loading={loading}
                />
              ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
};

/**
 * Individual Product Card Component
 */
const ProductCard = ({ product, onCheckout, loading }) => {
  const formatPrice = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  return (
    <div className="product-card">
      <div className="product-image">
        <img src={product.image} alt={product.name} />
      </div>
      <div className="product-details">
        <h3 className="product-name">{product.name}</h3>
        <p className="product-description">{product.description}</p>
        <div className="product-footer">
          <span className="product-price">{formatPrice(product.price)}</span>
          <button
            className="checkout-button"
            onClick={() => onCheckout(product)}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Buy Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripeCheckout;
